import { useStores } from '@littlebird/sdk';
import { useNavigate } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { WebSidebarToggle } from '@/components/blocks/Layout/WebSidebarToggle';
import { Button, IconButton } from '@/components/ui';
import { Skeleton } from '@/components/ui/skeleton';
import {
  ArtifactPanelShell,
  FilePreviewContent,
  type HtmlPreviewMode,
  HtmlPreviewModeToggle,
  useArtifactContent,
  useCopyFileText,
} from '@/features/artifacts';
import { CanvasExportMenu } from '@/features/canvas/CanvasExportMenu';
import { useOpenChat } from '@/features/chat/use-open-chat';
import { SharePublishedSitePopover } from '@/features/published-sites/SharePublishedSitePopover';
import { SandboxTerminalPanel } from '@/features/sprites/SandboxTerminalPanel';
import { SandboxTerminalToggle } from '@/features/sprites/SandboxTerminalToggle';
import { SpriteFrame } from '@/features/sprites/SpriteFrame';
import { type HydratedFile, useHydratedFile } from '@/hooks/useHydratedFile';
import { logError } from '@/lib/errorLogging';
import { userStore } from '@/stores/user.store';
import { inElectron } from '@/utils';
import { trackEvent } from '@/utils/analytics';
import { downloadFileWithFeedback, saveBlob } from '@/utils/file-io';
import { stripFileExtension } from '@/utils/files';

import { canShareSprite, spriteTypeOf } from './sprite-presentation';
import {
  deleteSpriteWithFeedback,
  fetchSprite,
  spriteArtifactType,
  type SpriteDetail,
} from './sprites.store';

interface SpriteViewProps {
  spriteId: string;
}

/**
 * Full-height render of a saved sprite. File-backed sprites render with the same preview UI chat
 * uses (HTML/PDF/image/CSV/markdown); legacy sprites render their inline HTML in the sandboxed
 * (CSP-locked) iframe. Internal users can open a terminal into the originating chat's sandbox.
 */
export const SpriteView = observer(({ spriteId }: SpriteViewProps) => {
  const navigate = useNavigate();
  const { files: filesStore } = useStores();
  const { openChat, isOpening: isOpeningChat } = useOpenChat();
  const [sprite, setSprite] = useState<SpriteDetail | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(
    'loading'
  );
  // Bumping this remounts the preview, re-running a sprite's scripts / re-fetching its file.
  const [reloadKey, setReloadKey] = useState(0);
  const [htmlViewMode, setHtmlViewMode] = useState<HtmlPreviewMode>('preview');
  const artifact = useHydratedFile(sprite?.file_id);
  const { copied, copyFileText } = useCopyFileText('SpriteView');

  // Not gated on the sprites flag: users who lose it (e.g. after a downgrade) can still open what they made
  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setSprite(null);
    fetchSprite(spriteId)
      .then((detail) => {
        if (cancelled) return;
        setSprite(detail);
        setStatus('ready');
        trackEvent('SPRITE_OPENED', {
          sprite_id: detail.id,
          artifact_type: spriteArtifactType(detail.name),
          has_source_chat: !!detail.source_chat_id,
        });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        logError(error, 'Failed to load sprite', {
          context: 'SpriteView',
          metadata: { spriteId },
        });
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, [spriteId]);

  const sourceChatId = sprite?.source_chat_id;
  const canShare = !!sprite && canShareSprite(sprite);
  const spriteType = sprite
    ? spriteTypeOf(sprite.content_type, sprite.name)
    : null;
  const isCanvas = spriteType === 'canvas';
  const isHtml = spriteType === 'html';
  const isMarkdown = spriteType === 'markdown';
  const artifactFile = artifact.file;
  // Only canvas artifacts carry an embedded title; everything else falls back to the sprite name.
  const { title: artifactTitle } = useArtifactContent(artifactFile, {
    surface: 'preview',
    enabled: isCanvas,
  });
  const title =
    artifactTitle || stripFileExtension(sprite?.name ?? '') || 'Artifact';
  const canDownload =
    !!sprite &&
    !isCanvas &&
    (!!artifactFile || (!sprite.file_id && !!sprite.html));

  const handleDownload = async () => {
    if (!sprite) return;
    if (artifactFile) {
      await downloadFileWithFeedback(filesStore, artifactFile, 'SpriteView');
      return;
    }
    if (sprite.html) {
      const filename = sprite.name.toLowerCase().endsWith('.html')
        ? sprite.name
        : `${sprite.name}.html`;
      try {
        await saveBlob(
          new Blob([sprite.html], { type: 'text/html' }),
          filename
        );
      } catch (error) {
        logError(error, 'Failed to download sprite', {
          context: 'SpriteView',
        });
        toast.error('Failed to download file.');
      }
    }
  };

  const handleDelete = async () => {
    if (!sprite) return;
    if (await deleteSpriteWithFeedback(sprite, 'detail', 'SpriteView'))
      navigate({ to: '/chats' });
  };

  return (
    <ArtifactPanelShell
      variant="inline"
      title={title}
      leading={
        inElectron() ? (
          <IconButton
            size="sm"
            icon={Icons.back}
            tooltip="Back to artifacts"
            aria-label="Back to artifacts"
            onClick={() => navigate({ to: '/sprites' })}
          />
        ) : (
          <WebSidebarToggle />
        )
      }
      actions={
        <>
          {isHtml && sprite?.file_id && userStore.isInternalUser && (
            <HtmlPreviewModeToggle
              mode={htmlViewMode}
              onModeChange={setHtmlViewMode}
            />
          )}
          {canShare && sprite && (
            <SharePublishedSitePopover target={{ spriteId: sprite.id }}>
              <Button size="sm" variant="secondary" aria-label="Share artifact">
                Share
              </Button>
            </SharePublishedSitePopover>
          )}
        </>
      }
      iconActions={
        <>
          <IconButton
            size="sm"
            icon={Icons.refresh}
            tooltip="Reload"
            aria-label="Reload artifact"
            disabled={status !== 'ready'}
            onClick={() => setReloadKey((key) => key + 1)}
          />
          <SandboxTerminalToggle chatId={sourceChatId ?? undefined} />
          {isMarkdown && artifactFile && (
            <IconButton
              size="sm"
              icon={Icons.copy}
              tooltip={copied ? 'Copied to clipboard' : 'Copy'}
              aria-label="Copy markdown"
              onClick={() => void copyFileText(artifactFile)}
            />
          )}
          {isCanvas && artifactFile && status === 'ready' ? (
            <CanvasExportMenu file={artifactFile} />
          ) : (
            canDownload && (
              <IconButton
                size="sm"
                icon={Icons.download}
                tooltip="Download"
                aria-label="Download artifact"
                disabled={status !== 'ready'}
                onClick={() => void handleDownload()}
              />
            )
          )}
          {sourceChatId && (
            <IconButton
              size="sm"
              icon={Icons.chat}
              tooltip="Refine in chat"
              aria-label="Open originating chat"
              disabled={isOpeningChat}
              isLoading={isOpeningChat}
              onClick={() => {
                trackEvent('SPRITE_CHAT_OPENED', {
                  sprite_id: spriteId,
                  chat_id: sourceChatId,
                });
                void openChat(sourceChatId);
              }}
            />
          )}
          <IconButton
            size="sm"
            icon={Icons.trash}
            tooltip="Delete artifact"
            aria-label="Delete artifact"
            onClick={handleDelete}
          />
        </>
      }
    >
      <div className="flex-1 overflow-hidden">
        {status === 'loading' && (
          <Skeleton variant="low-emphasis" className="h-full w-full" />
        )}
        {status === 'error' && (
          <div className="h-full flex items-center justify-center text-sm text-foreground-muted">
            Failed to load this artifact. It may have been deleted.
          </div>
        )}
        {status === 'ready' && sprite && (
          <SpriteBody
            key={reloadKey}
            sprite={sprite}
            artifact={artifact}
            htmlViewMode={htmlViewMode}
          />
        )}
      </div>
      {/* Internal-only (opened via the header toggle): bottom-docked terminal
          into the originating chat's sandbox. */}
      <SandboxTerminalPanel chatId={sourceChatId ?? undefined} />
    </ArtifactPanelShell>
  );
});

const SpriteUnavailable = () => (
  <div className="h-full flex items-center justify-center text-sm text-foreground-muted">
    This file is no longer available.
  </div>
);

interface SpriteBodyProps {
  sprite: SpriteDetail;
  artifact: HydratedFile;
  htmlViewMode: HtmlPreviewMode;
}

/** Renders a sprite's content: file-backed via the shared preview, or legacy inline HTML. */
const SpriteBody = observer(
  ({ sprite, artifact, htmlViewMode }: SpriteBodyProps) => {
    if (sprite.file_id) {
      if (artifact.status === 'ready')
        return (
          <FilePreviewContent
            file={artifact.file}
            htmlViewMode={htmlViewMode}
          />
        );
      if (artifact.status === 'loading')
        return <Skeleton variant="low-emphasis" className="h-full w-full" />;
      return <SpriteUnavailable />;
    }
    if (sprite.html) {
      return (
        <SpriteFrame
          title={`Artifact ${sprite.name}`}
          html={sprite.html}
          className="h-full w-full border-0 bg-white"
        />
      );
    }
    return <SpriteUnavailable />;
  }
);
